import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { useAppSelector } from "@redux/hooks";
import { useAuditLogsList } from "./hooks/useAuditLogsList";
import { useAuditLogsFilterOptions } from "./hooks/useAuditLogsFilterOptions";
import { useAuditLogsUrlState } from "./hooks/useAuditLogsUrlState";
import { makeAuditLogColumns } from "./AuditLogsData";
import { AuditLogFiltersBar } from "./components/AuditLogFiltersBar";
import {
  FilterOptionsErrorState,
  NoAuditedProductsState,
} from "./components/AuditLogsStateViews";
import {
  buildAuditLogsQueryParams,
  hasActiveAuditLogFilters,
} from "./utils/auditLogsUrlSync";
import { AUDIT_LOGS_PAGE_SIZE_OPTIONS } from "./types";
import type { AuditLog, AuditLogsQueryParams } from "./types";

const AUDIT_LOG_DETAIL_PATH = "/support/audit-logs";
const ALLOWED_TENANT_TYPES = ["GLOBAL_ADMIN"];

const MainAuditLogsContent: React.FC = () => {
  const navigate = useNavigate();

  const {
    filters,
    patchFilters,
    clearFilters,
    setPage,
    setLimit,
  } = useAuditLogsUrlState();

  /* ── Tenant scoping (from the global header selection) ────────────────
   *
   * The audit-logs API is tenant-scoped at the backend, but the user can
   * switch tenants from the SideMenu/SideNav header at any time. We must
   * forward the active selection so the table reflects whichever tenant
   * the user is currently viewing.
   *
   * Semantics matching the rest of the dashboard:
   *   - selection.tenantId === 0  → no tenant active yet (initial state);
   *     omit `tenantId` so the backend falls back to the JWT tenant.
   *   - selection.tenantId  > 0   → forward it. The backend validates
   *     ownership (must equal the JWT tenant or be one of its customers).
   *     A partner viewing the aggregate has tenantId === partnerId; a
   *     specific customer view has tenantId === <customer's tenantId>.
   */
  const selectionTenantId = useAppSelector((s) => s.selection.tenantId);
  const tenantScope = selectionTenantId > 0 ? selectionTenantId : null;

  // Forward the currently selected productId so the backend can narrow
  // `resourceTypes` to that product's types only. The catalogue refetches
  // automatically when the user picks a different product because the hook
  // keys its cache by (tenantId, productId).
  const {
    data: filterOptions,
    isLoading: isLoadingFilterOptions,
    error: filterOptionsError,
    refetch: refetchFilterOptions,
  } = useAuditLogsFilterOptions({
    tenantId: tenantScope,
    productId: filters.productId,
  });

  const queryParams = useMemo<AuditLogsQueryParams>(() => {
    // Insertion order matters: backend addendum asks for `tenantId` to
    // come first in the query string for consistency with the rest of
    // the dashboard. Axios preserves key order.
    const out: AuditLogsQueryParams = {};
    if (tenantScope != null) out.tenantId = tenantScope;
    Object.assign(out, buildAuditLogsQueryParams(filters));
    return out;
  }, [filters, tenantScope]);

  /* When the tenant changes mid-page, reset to page 1 — the previous
   * page number rarely makes sense for a different tenant's dataset.
   *
   * `filters/options` refetches automatically because the hook keys its
   * cache by tenantId. The list refetches automatically because the
   * tenant goes into `queryParams` and `useAuditLogsList` keys its
   * effect on the serialized params.
   *
   * Filters themselves are kept: the user might have applied them
   * deliberately across tenants. Stale values that no longer exist in
   * the new catalogue are purged separately by the effect below.
   */
  const prevTenantScope = useRef<number | null>(tenantScope);
  useEffect(() => {
    if (prevTenantScope.current === tenantScope) return;
    prevTenantScope.current = tenantScope;
    if (filters.page !== 1) setPage(1);
  }, [tenantScope, filters.page, setPage]);

  /* Purge filter values that became invalid when `/filters/options`
   * reloaded — typically after a tenant switch, where a previously
   * available product or resourceType is no longer in the new catalogue.
   *
   * Why it matters:
   *   - The backend rejects unknown enum values with 400.
   *   - A "ghost" chip in the URL would silently keep the table empty
   *     until the user noticed and removed it.
   *
   * `actions` and `sources` are global (per backend addendum), so they
   * never need pruning here.
   */
  useEffect(() => {
    if (filterOptions == null) return;
    const patch: Record<string, unknown> = {};

    if (
      filters.productId != null &&
      !filterOptions.products.some((p) => p.id === filters.productId)
    ) {
      patch.productId = null;
    }
    if (
      filters.resourceType != null &&
      !filterOptions.resourceTypes.includes(filters.resourceType)
    ) {
      patch.resourceType = null;
    }

    if (Object.keys(patch).length > 0) {
      patchFilters(patch as Parameters<typeof patchFilters>[0]);
    }
  }, [filterOptions, filters.productId, filters.resourceType, patchFilters]);

  const {
    data,
    pagination,
    isFetching,
    hasFetched,
    error,
    refetch,
  } = useAuditLogsList(queryParams);

  const handleViewRow = useCallback(
    (row: AuditLog) => {
      // Pass the row through `location.state` so the detail page can
      // render instantly on click (the hook in Phase 4 will treat it as
      // `seedData` and skip the round-trip).
      navigate(`${AUDIT_LOG_DETAIL_PATH}/${row.id}`, {
        state: { auditLog: row },
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => makeAuditLogColumns({ onView: handleViewRow }),
    [handleViewRow]
  );

  const handlePageChange = useCallback((next: number) => setPage(next), [setPage]);
  const handleRowsPerPageChange = useCallback(
    (nextLimit: number) => setLimit(nextLimit),
    [setLimit]
  );

  const handleFiltersChange = useCallback(
    (patch: Parameters<typeof patchFilters>[0]) => patchFilters(patch),
    [patchFilters]
  );

  const totalCount = pagination?.total ?? 0;
  const showInitialLoading = !hasFetched && isFetching;
  const showOverlay = hasFetched && isFetching;
  const isEmpty = hasFetched && !isFetching && data.length === 0;

  /* ── Page-level guards ────────────────────────────────────────────────
   *
   * Order matters. We resolve these BEFORE deciding to render the filter
   * bar / table because each one means the page can't proceed until it
   * clears.
   *
   *   1. Filter-options endpoint failed AND we have nothing cached →
   *      block with retry. Without this catalogue the dropdowns would
   *      be empty and the productId we send would be unverifiable.
   *
   *   2. Catalogue loaded but `products` is empty → tenant has no
   *      audit-enabled products. Blocking ensures the user gets the
   *      product-team's wording instead of a generic empty table.
   *
   *   3. Otherwise fall through to the normal page (filters + table).
   */
  const blockWithFilterOptionsError =
    filterOptionsError != null && filterOptions == null;

  const blockWithNoProducts =
    !isLoadingFilterOptions &&
    filterOptions != null &&
    filterOptions.products.length === 0;

  return (
    <div className="relative flex h-full flex-col overflow-hidden p-4">
      <header className="mb-4 flex-shrink-0">
        <h1 className="text-2xl font-semibold text-semantic-text-primary">
          Audit Logs
        </h1>
        <p className="mt-1 text-sm text-semantic-text-secondary">
          Cross-product history of write operations performed on your
          tenant&apos;s resources.
        </p>
      </header>

      <div className="relative min-h-0 flex-1">
        {blockWithFilterOptionsError ? (
          <FilterOptionsErrorState
            message={filterOptionsError as string}
            onRetry={refetchFilterOptions}
          />
        ) : blockWithNoProducts ? (
          <NoAuditedProductsState />
        ) : (
          <GenericCard
            isCollapsible={false}
            className="flex h-full flex-col"
            bodyContent={
              <div className="flex h-full min-h-0 flex-col">
                <AuditLogFiltersBar
                  value={filters}
                  options={filterOptions}
                  optionsLoading={isLoadingFilterOptions}
                  onChange={handleFiltersChange}
                  onClear={clearFilters}
                />

                {/* Cached options are still usable when a refresh fails;
                    surface the error inline rather than blocking the page. */}
                {filterOptionsError && filterOptions ? (
                  <InlineErrorBanner
                    title="Filter options out of date"
                    message={filterOptionsError}
                    onRetry={refetchFilterOptions}
                  />
                ) : null}

                {error ? (
                  <InlineErrorBanner
                    title="Failed to load audit logs"
                    message={error}
                    onRetry={refetch}
                  />
                ) : null}

                <div className="relative flex min-h-0 flex-1 flex-col">
                  {showOverlay ? <TableOverlay /> : null}

                  {isEmpty && !error ? (
                    <EmptyResults
                      hasFilters={hasActiveAuditLogFilters(filters)}
                      onClear={clearFilters}
                    />
                  ) : (
                    <GenericTable<AuditLog>
                      columns={columns}
                      data={data}
                      serverSide
                      totalCount={totalCount}
                      currentPage={filters.page}
                      rowsPerPage={filters.limit}
                      onPageChange={handlePageChange}
                      onRowsPerPageChange={handleRowsPerPageChange}
                      rowsPerPageOptions={AUDIT_LOGS_PAGE_SIZE_OPTIONS}
                      loading={showInitialLoading}
                      loadingSkeletonRows={filters.limit}
                      loadingSkeletonCols={columns.length}
                      onRowClick={handleViewRow}
                      rowKey={(row) => row.id}
                      width="100%"
                      tableClassName="bg-semantic-bg-primary"
                      maxTableBodyHeight="100%"
                      verticalAlign="center"
                    />
                  )}
                </div>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

interface InlineErrorBannerProps {
  title: string;
  message: string;
  onRetry: () => void;
}

const InlineErrorBanner: React.FC<InlineErrorBannerProps> = ({
  title,
  message,
  onRetry,
}) => (
  <div
    role="alert"
    className="mb-3 flex flex-shrink-0 items-start justify-between gap-3 rounded-md border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary p-3"
  >
    <div className="min-w-0">
      <p className="text-sm font-medium text-semantic-text-danger">{title}</p>
      <p className="text-xs text-semantic-text-danger/80">{message}</p>
    </div>
    <button
      type="button"
      onClick={onRetry}
      className="flex-shrink-0 rounded-md border border-semantic-border-danger-primary px-3 py-1 text-xs font-medium text-semantic-text-danger hover:bg-semantic-bg-danger-secondary"
    >
      Retry
    </button>
  </div>
);

const TableOverlay: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-semantic-bg-primary/40">
    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-semantic-border-secondary bg-semantic-bg-primary px-3 py-1 shadow-sm">
      <span className="block h-3 w-3 animate-spin rounded-full border-2 border-semantic-border-secondary border-t-semantic-text-brand" />
      <span className="text-xs text-semantic-text-secondary">Updating…</span>
    </div>
  </div>
);

interface EmptyResultsProps {
  hasFilters: boolean;
  onClear: () => void;
}

const EmptyResults: React.FC<EmptyResultsProps> = ({ hasFilters, onClear }) => (
  <div className="flex flex-1 items-center justify-center p-6">
    <div className="text-center">
      <p className="text-sm font-medium text-semantic-text-primary">
        {hasFilters
          ? "No audit log entries match these filters"
          : "No audit activity recorded yet"}
      </p>
      <p className="mt-1 text-xs text-semantic-text-secondary">
        {hasFilters
          ? "Try widening the date range or removing some filters."
          : "Activity from any of your products will show up here."}
      </p>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-3 rounded-md border border-semantic-border-secondary px-3 py-1 text-xs font-medium text-semantic-text-brand hover:bg-semantic-state-active"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  </div>
);

/**
 * Wraps the whole page in `RequireTenantType` so that:
 *   - The API hooks inside `MainAuditLogsContent` only fire once tenant
 *     access is confirmed (RequireTenantType doesn't render children
 *     until `isAllowed === true`).
 *   - Direct URL access by a non-admin redirects without any flash of
 *     content. The SideMenu hides the entry as well, so this is the
 *     second line of defense.
 */
const MainAuditLogs: React.FC = () => (
  <RequireTenantType allowedTypes={ALLOWED_TENANT_TYPES}>
    <MainAuditLogsContent />
  </RequireTenantType>
);

export default MainAuditLogs;
